import {
  TravelPlacePricingAndBookingDetailsPayload,
  PreferredHotelProviderType,
  PriceComparisonPayload,
  PriceComparisonType,
  TravelPlacePricingAndBookingDetailsFragment,
  TravelPlacePricingAndBookingDetailsFragmentDoc,
  TravelCurrencyCode,
  useTravelPlacesPricingAndBookingDetailsQuery,
  useTravelPlacesPricingAndBookingDetailsLazyQuery,
  AccommodationCatalogStayDatesInput,
  AccommodationCatalogUnitsOccupancyInput,
  InputMaybe,
  TravelJoyPlacePricingAndBookingDetailsFragmentDoc,
  TravelJoyPlacePricingAndBookingDetailsFragment
} from '@graphql/generated';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { ApolloClient, ApolloError, makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { addAction, addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCheckInCheckoutDates } from '../useCheckInCheckoutDates';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';
import { getCalculatedPrice } from '@shared/utils/accommodationPrice';
import { useMinimumPercentageOffToDisplay } from '../useMinimumPercentageOffToDisplay';

const queryStatusVar = makeVar<{
  loading: boolean;
  called: boolean;
  error?: ApolloError;
}>({ loading: false, called: false, error: undefined });

const isValidPriceComparisonType = (value?: InputMaybe<PriceComparisonType>): value is PriceComparisonType =>
  Object.values(PriceComparisonType).includes(value as PriceComparisonType);

const isValidPreferredHotelProviderType = (value?: InputMaybe<PreferredHotelProviderType>): value is PreferredHotelProviderType =>
  Object.values(PreferredHotelProviderType).includes(value as PreferredHotelProviderType);

const isValidMarginPercentage = (value?: InputMaybe<number>): value is number => typeof value === 'number' && !isNaN(value) && value >= 0 && value <= 100;

const isValidPriceComparePayload = (payload: PriceComparisonPayload | undefined) => {
  if (!payload) return false;
  return (
    isValidPriceComparisonType(payload.comparisonType) &&
    isValidPreferredHotelProviderType(payload.preferredProvider) &&
    isValidMarginPercentage(payload.preferredProviderPriceMaxExtraPercent)
  );
};

/**
 *
 * @param travelPlaceIds - array of travelPlaceIds to fetch pricing
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 */
export const useTravelPlacesPricingAndBookingDetails = (travelPlaceIds: string[], travelPlaceIdType: 'accommodationPlace' | 'joyPlace', skipOnInitialLoad = false) => {
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const { checkIn: checkInDate, checkOut: checkOutDate } = useCheckInCheckoutDates({ eventDate });

  const [currencyCode, { loading: loadingCurrencyCode }] = useTravelWhiteLabelCurrency();
  const { value: localeCurrencyOnOrOff, loading: loadingUseLocaleCurrencyFlag } = useRemoteFeatureFlag('localeBasedHotelCurrency');
  const {
    value: accommodationsGuestPriceCompareType,
    loading: accommodationsGuestPriceCompareTypeLoading,
    payload: accommodationsGuestPriceCompareTypeLoadingPayload
  } = useRemoteFeatureFlag('accommodationsGuestPriceCompareType');

  const preferredProviderType = accommodationsGuestPriceCompareType as PreferredHotelProviderType;
  const priceComparisonPayload = useMemo(() => {
    const remotePayload = (Object(accommodationsGuestPriceCompareTypeLoadingPayload) as PriceComparisonPayload) || {};
    return {
      comparisonType: remotePayload.comparisonType,
      preferredProvider: remotePayload.preferredProvider,
      preferredProviderPriceMaxExtraPercent: remotePayload.preferredProviderPriceMaxExtraPercent
    };
  }, [accommodationsGuestPriceCompareTypeLoadingPayload]);

  const payload: TravelPlacePricingAndBookingDetailsPayload | undefined = useMemo(() => {
    if (loadingUseLocaleCurrencyFlag || loadingCurrencyCode || loadingEventInfo || !eventInfo?.eventId) {
      return undefined;
    }
    // Validate priceComparisonPayload values come from amplitude
    const shouldUsePriceComparison = accommodationsGuestPriceCompareType === 'pricecomparison';
    let validPriceComparisonPayload: PriceComparisonPayload | undefined;
    if (shouldUsePriceComparison && isValidPriceComparePayload(priceComparisonPayload)) {
      validPriceComparisonPayload = priceComparisonPayload;
    }
    // Validate preferredProviderType values come from amplitude
    let validPreferredProviderType: PreferredHotelProviderType | undefined;
    if ((!shouldUsePriceComparison || !validPriceComparisonPayload) && isValidPreferredHotelProviderType(preferredProviderType)) {
      validPreferredProviderType = preferredProviderType;
    }
    return {
      currencyCode: ((localeCurrencyOnOrOff === 'on' ? currencyCode : TravelCurrencyCode.USD) || TravelCurrencyCode.USD) as TravelCurrencyCode,
      dates: {
        checkIn: {
          // validated with skip
          high: checkInDate,
          low: checkInDate
        },
        // validated with skip
        checkOut: {
          high: checkOutDate,
          low: checkOutDate
        }
      },
      occupancies: {
        occupancies: [
          {
            adults: 2,
            childrenAges: []
          }
        ],
        unitCount: 1
      },
      metadata: {
        eventId: eventInfo.eventId,
        ...(validPriceComparisonPayload && { priceComparisonPayload: validPriceComparisonPayload }),
        ...(validPreferredProviderType && { preferredProviderType: validPreferredProviderType })
      },
      ...(travelPlaceIdType === 'accommodationPlace' && { accommodationPlaceIds: travelPlaceIds })
    };
  }, [
    checkInDate,
    checkOutDate,
    travelPlaceIds,
    loadingUseLocaleCurrencyFlag,
    loadingEventInfo,
    eventInfo?.eventId,
    loadingCurrencyCode,
    accommodationsGuestPriceCompareType,
    priceComparisonPayload,
    preferredProviderType,
    localeCurrencyOnOrOff,
    currencyCode,
    travelPlaceIdType
  ]);

  const { data, loading, called, error } = useTravelPlacesPricingAndBookingDetailsQuery({
    batchMode: 'fast',
    variables: {
      payload: payload!
    },
    skip: skipOnInitialLoad || !payload || !travelPlaceIds.length,
    onError(error) {
      addError(error, {
        source: 'Pricing api failed',
        context: 'travelPlacesPricingAndBookingDetails',
        errorMessage: error.message,
        checkInDate,
        checkOutDate,
        currencyCode: payload?.currencyCode,
        metadata: payload?.metadata,
        accommodationPlaceIds: payload?.accommodationPlaceIds,
        joyPlaceIds: payload?.joyPlaceIds
      });
    },
    onCompleted(data) {
      const accommodationPlaces = data?.travelPlacesPricingAndBookingDetails?.accommodationPlaces;
      if (!accommodationPlaces?.length) {
        addAction('Pricing api empty results', {
          context: 'travelPlacesPricingAndBookingDetails',
          checkInDate,
          checkOutDate,
          currencyCode: payload?.currencyCode,
          metadata: payload?.metadata,
          accommodationPlaceIds: payload?.accommodationPlaceIds,
          joyPlaceIds: payload?.joyPlaceIds
        });
      }
    }
  });

  const [
    getTravelPlacesPricingAndBookingDetails,
    {
      data: lazyData,
      loading: loadingGetTravelPlacesPricingAndBookingDetails,
      called: calledGetTravelPlacesPricingAndBookingDetails,
      error: errorGetTravelPlacesPricingAndBookingDetails
    }
  ] = useTravelPlacesPricingAndBookingDetailsLazyQuery({
    batchMode: 'fast',
    onError: error => {
      addError(error, {
        context: 'useTravelPlacesPricingAndBookingDetailsQuery'
      });
    }
  });

  const fetchTravelPricingData = useCallback(
    (travelIdsToFetch: string[]) => {
      if (!travelIdsToFetch.length) {
        return;
      }

      getTravelPlacesPricingAndBookingDetails({
        variables: {
          payload: {
            ...payload,
            currencyCode: payload?.currencyCode as TravelCurrencyCode,
            dates: payload?.dates as AccommodationCatalogStayDatesInput,
            occupancies: payload?.occupancies as AccommodationCatalogUnitsOccupancyInput,
            ...(travelPlaceIdType === 'accommodationPlace' && { accommodationPlaceIds: travelIdsToFetch }),
            ...(travelPlaceIdType === 'joyPlace' && { joyPlaceIds: travelIdsToFetch })
          }!
        }
      });
    },
    [payload, travelPlaceIdType, getTravelPlacesPricingAndBookingDetails]
  );

  useEffect(() => {
    queryStatusVar({
      loading: loading || loadingUseLocaleCurrencyFlag || accommodationsGuestPriceCompareTypeLoading || loadingGetTravelPlacesPricingAndBookingDetails,
      called: called || calledGetTravelPlacesPricingAndBookingDetails,
      error: error || errorGetTravelPlacesPricingAndBookingDetails
    });
  }, [
    loading,
    loadingUseLocaleCurrencyFlag,
    accommodationsGuestPriceCompareTypeLoading,
    called,
    error,
    loadingGetTravelPlacesPricingAndBookingDetails,
    calledGetTravelPlacesPricingAndBookingDetails,
    errorGetTravelPlacesPricingAndBookingDetails
  ]);

  return {
    data: data || lazyData,
    loading: loading || loadingUseLocaleCurrencyFlag || accommodationsGuestPriceCompareTypeLoading || loadingGetTravelPlacesPricingAndBookingDetails,
    called: called || calledGetTravelPlacesPricingAndBookingDetails,
    error: error || errorGetTravelPlacesPricingAndBookingDetails,
    checkInDate,
    checkOutDate,
    fetchTravelPricingData
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, travelPlaceId: string, travelPlaceIdType: 'accommodationPlace' | 'joyPlace') => {
  // Define the fragment
  const fragment = TravelPlacePricingAndBookingDetailsFragmentDoc;
  const joyPlaceFragment = TravelJoyPlacePricingAndBookingDetailsFragmentDoc;

  // Generate the cache ID based on the type and key fields
  let cacheId = `AccommodationPlacePricingAndBookingDetails:{"accommodationPlace":{"id":"${travelPlaceId}"}}`;
  if (travelPlaceIdType === 'joyPlace') {
    cacheId = `JoyPlacePricingAndBookingDetails:{"joyPlace":{"id":"${travelPlaceId}"}}`;
  }

  // Read the fragment from the cache
  const data = client.cache.readFragment<TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment>({
    id: cacheId,
    fragment: travelPlaceIdType === 'joyPlace' ? joyPlaceFragment : fragment
  });

  return data;
};

/**
 *
 * @param travelPlaceId - accommodationPlaceId to fetch pricing for
 * @param travelPlaceIdType = accommodationPlace | joyPlace - To identify which type of travelPlaceIds either accommodationPlace or joyPlace
 * @returns
 */
export const useTravelPlacesPricingAndBookingDetailsFragment = (
  travelPlaceId: Maybe<string>,
  travelPlaceIdType: 'accommodationPlace' | 'joyPlace',
  skip?: boolean
): {
  price: Maybe<string>;
  strikeOutPrice: Maybe<string>;
  percentageOff: Maybe<number>;
  data: Maybe<TravelPlacePricingAndBookingDetailsFragment | TravelJoyPlacePricingAndBookingDetailsFragment>;
  loading: boolean;
  error?: Error;
} => {
  const complaintRef = useRef<boolean>(false);
  const client = useApolloClient();
  const locale = useLocaleContext();
  const { loading, error } = useReactiveVar(queryStatusVar);
  const { minimumPercentageOffToDisplayValue, loadingMinimumPercentageOffToDisplay } = useMinimumPercentageOffToDisplay();

  if (loading || loadingMinimumPercentageOffToDisplay) {
    return { price: undefined, strikeOutPrice: undefined, percentageOff: undefined, data: undefined, loading: true };
  }
  if (error) {
    return { price: null, strikeOutPrice: undefined, percentageOff: undefined, data: undefined, loading: false, error: error };
  }
  if (skip) {
    return { price: undefined, strikeOutPrice: undefined, percentageOff: undefined, data: undefined, loading: false };
  }
  const data = readFragmentFromCache(client, travelPlaceId!, travelPlaceIdType);

  const { price, strikeOutPrice, percentageOff } = getCalculatedPrice(data, complaintRef, locale, minimumPercentageOffToDisplayValue, travelPlaceId);

  return { price, strikeOutPrice, percentageOff, data, loading: loading };
};
